import { Plan } from '../services/api';

/** Match WhatzPro subscription plans regardless of Razorpay name casing. */
export const isWhatzProPlanName = (name: string): boolean => {
  const n = String(name || '').toLowerCase();
  const isSingle = n.includes('single') && n.includes('sub');
  const isMulti = n.includes('11+') && n.includes('sub');
  const hasPeriod = n.includes('monthly') || n.includes('yearly');
  return (isSingle || isMulti) && hasPeriod;
};

type PlanPeriodSource = {
  item?: { name?: string };
  period?: string;
};

export const planMatchesPeriod = (
  planOrName: PlanPeriodSource | string,
  period: 'Monthly' | 'Yearly'
): boolean => {
  const target = period.toLowerCase();
  if (typeof planOrName === 'string') {
    return planOrName.toLowerCase().includes(target);
  }
  const name = String(planOrName?.item?.name || '').toLowerCase();
  const razorpayPeriod = String(planOrName?.period || '').toLowerCase();
  return name.includes(target) || razorpayPeriod === target;
};

export const isSingleSubAccountPlan = (name: string): boolean => {
  const n = String(name || '').toLowerCase();
  return n.includes('single') && n.includes('sub');
};

export const is11PlusPlan = (name: string): boolean => {
  const n = String(name || '').toLowerCase();
  return n.includes('11+') && n.includes('sub');
};

/** Marketing feature bullets — matches product pricing UI */
export const PLAN_FEATURE_LIST = [
  'Everything in Single Sub-Account',
  'Minimum 11 sub-accounts',
  'Priority Support',
  'Advanced Analytics',
  'Bulk Messaging Features',
];

const DISPLAY_PRICES = {
  single: { monthly: 7.99, yearly: 79.9 },
  elevenPlus: { monthly: 5.99, yearly: 59.9 },
} as const;

export const formatPlanPrice = (amount: number): string => `$${Number(amount).toFixed(2)}`;

const planTypePeriodKey = (plan: Plan): string | null => {
  const name = String(plan.item?.name || '');
  const isSingle = isSingleSubAccountPlan(name);
  const isMulti = is11PlusPlan(name);
  if (!isSingle && !isMulti) return null;
  const period = planMatchesPeriod(plan, 'Yearly') ? 'yearly' : 'monthly';
  return `${isSingle ? 'single' : '11plus'}-${period}`;
};

/** Keep exactly one Single + one 11+ plan per billing period (monthly/yearly). */
export const dedupePlansByType = (plans: Plan[]): Plan[] => {
  const byKey = new Map<string, Plan>();
  for (const plan of plans) {
    const key = planTypePeriodKey(plan);
    if (!key || byKey.has(key)) continue;
    byKey.set(key, plan);
  }
  const order = ['11plus-monthly', 'single-monthly', '11plus-yearly', 'single-yearly'];
  return order.map(key => byKey.get(key)).filter(Boolean) as Plan[];
};

export const filterSubscriptionPlans = (plans: Plan[]): Plan[] => {
  if (!Array.isArray(plans) || plans.length === 0) return [];
  const matched = plans.filter(p => isWhatzProPlanName(p.item?.name));
  const source = matched.length > 0 ? matched : plans;
  return dedupePlansByType(source);
};

export const getPlanDisplayAmount = (plan: Plan): number => {
  const name = String(plan.item?.name || '');
  const isYearly = planMatchesPeriod(plan, 'Yearly');
  const prices = isSingleSubAccountPlan(name) ? DISPLAY_PRICES.single : DISPLAY_PRICES.elevenPlus;
  return isYearly ? prices.yearly : prices.monthly;
};

export const normalizePlanName = (name: string): string =>
  String(name || '').trim().toLowerCase();

export const isActivePlanMatch = (planName: string, activePlanName: string): boolean =>
  normalizePlanName(planName) === normalizePlanName(activePlanName);

/** Map stored subscription planId to the plan card id (handles legacy test-plan id). */
export const resolveSubscriptionPlanId = (sub: {
  planId?: string;
  planName?: string;
}): string | null => {
  const planId = String(sub.planId || '').trim();
  if (!planId) return null;
  if (planId !== 'test-plan') return planId;

  const name = normalizePlanName(sub.planName || '');
  if (name.includes('11+') && name.includes('yearly')) return 'test-plan-11plus-yearly';
  if (name.includes('11+')) return 'test-plan-11plus-monthly';
  if (name.includes('yearly')) return 'test-plan-single-yearly';
  return 'test-plan-single-monthly';
};

type SubscriptionLike = {
  planId?: string;
  planName?: string;
  status?: string;
};

/** True only when this exact plan card matches a paid subscription (by planId, not name). */
export const isPlanCardCurrent = (
  plan: Plan,
  subscriptions: SubscriptionLike[],
  isPaid: (status?: string) => boolean
): boolean => {
  const paidSubs = subscriptions.filter(sub => isPaid(sub.status));
  if (paidSubs.length === 0) return false;

  const planId = String(plan.id || '').trim();
  if (!planId) return false;

  return paidSubs.some(sub => resolveSubscriptionPlanId(sub) === planId);
};

export const getPaidSubscriptions = (
  subscriptions: SubscriptionLike[],
  isPaid: (status?: string) => boolean
): SubscriptionLike[] => subscriptions.filter(sub => isPaid(sub.status));

/** Demo-only subscriptions — must not count as a real purchase. */
export const isTestSubscription = (sub: {
  razorpaySubscriptionId?: string;
  planId?: string;
}): boolean => {
  const razorpayId = String(sub.razorpaySubscriptionId || '');
  const planId = String(sub.planId || '');
  return razorpayId.startsWith('test-') || planId === 'test-plan' || planId.startsWith('test-plan-');
};

export const getStoredSubscriptionDisplayPrice = (sub: {
  planPrice?: string | number;
  planName?: string;
}): string => {
  const raw = Number(sub.planPrice ?? 0);
  if (raw > 0) return formatPlanPrice(raw / 100);

  const name = String(sub.planName || '');
  const isYearly = planMatchesPeriod(name, 'Yearly');
  const prices = isSingleSubAccountPlan(name) ? DISPLAY_PRICES.single : DISPLAY_PRICES.elevenPlus;
  return formatPlanPrice(isYearly ? prices.yearly : prices.monthly);
};
